/**
 * cart-billing-form.js
 * Toggle Privato/Azienda, validazione, save via cart attributes (/cart/update.js)
 * Sync bidirezionale cart page ↔ cart drawer via sessionStorage
 */

(function () {
  'use strict';

  // Guard: evita doppia esecuzione se lo script viene incluso
  // sia dalla pagina carrello che dal drawer snippet.
  if (window.__thbCartBillingLoaded) return;
  window.__thbCartBillingLoaded = true;

  const STORAGE_KEY = 'thb_billing_data';

  // Tutte le chiavi billing che gestiamo — usate per pulizia selettiva
  const BILLING_ATTR_KEYS = [
    'Fattura',
    'Tipo soggetto',
    'Codice Fiscale',
    'Partita IVA',
    'Codice SDI',
    'PEC',
  ];

  /* ------------------------------------------------------------------ */
  /*  Validazione                                                         */
  /* ------------------------------------------------------------------ */

  function normalizeCF(v) {
    return v.trim().toUpperCase().replace(/\s+/g, '');
  }
  function validateCF(v) {
    return /^[A-Z0-9]{16}$/.test(normalizeCF(v));
  }
  function validatePIVA(v) {
    return /^\d{11}$/.test(v.trim());
  }
  function validateEmail(v) {
    return v.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  }

  function setInputState(input, state) {
    input.classList.toggle('is-valid',   state === 'valid');
    input.classList.toggle('is-invalid', state === 'invalid');
  }

  function showError(errEl, show) {
    if (!errEl) return;
    errEl.classList.toggle('is-visible', show);
  }

  /* ------------------------------------------------------------------ */
  /*  Cart attributes API                                                 */
  /* ------------------------------------------------------------------ */

  async function getCartAttributes() {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    return cart.attributes || {};
  }

  async function updateCartAttributes(type, fields) {
    const existing = await getCartAttributes();

    // Rimuovi tutti i nostri attributi billing precedenti
    BILLING_ATTR_KEYS.forEach((k) => delete existing[k]);

    // Aggiungi i nuovi
    const billing = {
      'Fattura': 'Richiesta',
      'Tipo soggetto': type === 'azienda' ? 'Azienda' : 'Privato',
      ...fields,
    };

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { ...existing, ...billing } }),
    });
  }

  async function clearCartAttributes() {
    const existing = await getCartAttributes();
    BILLING_ATTR_KEYS.forEach((k) => delete existing[k]);
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: existing }),
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers UI                                                          */
  /* ------------------------------------------------------------------ */

  function getActiveType(ctx) {
    const activeBtn = document.querySelector(`.billing-type-btn--active[data-ctx="${ctx}"]`);
    return activeBtn ? activeBtn.dataset.type : 'privato';
  }

  function switchType(ctx, type) {
    document.querySelectorAll(`.billing-type-btn[data-ctx="${ctx}"]`).forEach((btn) => {
      btn.classList.toggle('billing-type-btn--active', btn.dataset.type === type);
    });
    const privato = document.getElementById(`billing-fields-privato--${ctx}`);
    const azienda = document.getElementById(`billing-fields-azienda--${ctx}`);
    if (privato) privato.style.display = type === 'privato' ? '' : 'none';
    if (azienda) azienda.style.display = type === 'azienda' ? '' : 'none';
  }

  function collectFields(ctx, type) {
    const container = document.getElementById(`billing-fields-${type}--${ctx}`);
    if (!container) return {};
    const fields = {};
    container.querySelectorAll('.billing-input').forEach((input) => {
      const key = input.dataset.billingKey;
      const val = input.value.trim();
      if (key && val) {
        fields[key] = key === 'Codice Fiscale' ? normalizeCF(val) : val;
      }
    });
    return fields;
  }

  function fillFields(ctx, type, fields) {
    for (const [key, val] of Object.entries(fields)) {
      const input = document.querySelector(
        `[data-billing-key="${key}"][data-billing-ctx="${ctx}"]`
      );
      if (input && val) {
        input.value = val;
        setInputState(input, 'valid');
      }
    }
  }

  function showSavedCard(ctx, type, fields) {
    const card = document.getElementById(`billing-saved--${ctx}`);
    const text = document.getElementById(`billing-saved-text--${ctx}`);
    if (!card || !text) return;
    const label = type === 'azienda' ? 'Azienda' : 'Privato';
    let html = `<strong>${label}</strong>`;
    for (const [k, v] of Object.entries(fields)) {
      if (v) html += `${k}: <b>${v}</b><br>`;
    }
    text.innerHTML = html;
    card.classList.add('is-visible');
  }

  function hideSavedCard(ctx) {
    const card = document.getElementById(`billing-saved--${ctx}`);
    if (card) card.classList.remove('is-visible');
  }

  /* ------------------------------------------------------------------ */
  /*  Validazione completa al submit                                      */
  /* ------------------------------------------------------------------ */

  function validateAll(ctx, type) {
    let ok = true;

    if (type === 'privato') {
      const cfInput = document.getElementById(`billing-cf--${ctx}`);
      const errEl   = document.getElementById(`err-cf--${ctx}`);
      if (cfInput) {
        const valid = cfInput.value.trim() !== '' && validateCF(cfInput.value);
        setInputState(cfInput, valid ? 'valid' : 'invalid');
        showError(errEl, !valid);
        if (!valid) ok = false;
      }
    } else {
      const pivaInput = document.getElementById(`billing-piva--${ctx}`);
      const errPiva   = document.getElementById(`err-piva--${ctx}`);
      if (pivaInput) {
        const valid = pivaInput.value.trim() !== '' && validatePIVA(pivaInput.value);
        setInputState(pivaInput, valid ? 'valid' : 'invalid');
        showError(errPiva, !valid);
        if (!valid) ok = false;
      }
      const sdiInput = document.getElementById(`billing-sdi--${ctx}`);
      const pecInput = document.getElementById(`billing-pec--${ctx}`);
      const errPec   = document.getElementById(`err-pec--${ctx}`);
      if (sdiInput && pecInput) {
        const hasSdi = sdiInput.value.trim() !== '';
        const hasPec = pecInput.value.trim() !== '';
        if (!hasSdi && !hasPec) {
          setInputState(pecInput, 'invalid');
          if (errPec) { errPec.textContent = 'Inserire SDI oppure PEC'; showError(errPec, true); }
          ok = false;
        } else if (hasPec && !validateEmail(pecInput.value)) {
          setInputState(pecInput, 'invalid');
          if (errPec) { errPec.textContent = 'Indirizzo PEC non valido'; showError(errPec, true); }
          ok = false;
        } else {
          setInputState(pecInput, hasPec ? 'valid' : '');
          showError(errPec, false);
        }
      }
    }

    return ok;
  }

  /* ------------------------------------------------------------------ */
  /*  Check silenzioso — senza side-effect UI                             */
  /*  Ritorna true solo se i campi obbligatori del tab attivo            */
  /*  risultano validi secondo le stesse regole di validateAll().        */
  /* ------------------------------------------------------------------ */

  function isFormValid(ctx, type) {
    if (type === 'privato') {
      const cfInput = document.getElementById(`billing-cf--${ctx}`);
      if (!cfInput) return false;
      return cfInput.value.trim() !== '' && validateCF(cfInput.value);
    }
    // azienda
    const pivaInput = document.getElementById(`billing-piva--${ctx}`);
    const sdiInput  = document.getElementById(`billing-sdi--${ctx}`);
    const pecInput  = document.getElementById(`billing-pec--${ctx}`);
    if (!pivaInput || !sdiInput || !pecInput) return false;

    const pivaOk = pivaInput.value.trim() !== '' && validatePIVA(pivaInput.value);
    if (!pivaOk) return false;

    const hasSdi = sdiInput.value.trim() !== '';
    const hasPec = pecInput.value.trim() !== '';
    if (!hasSdi && !hasPec) return false;
    if (hasPec && !validateEmail(pecInput.value)) return false;

    return true;
  }

  /* ------------------------------------------------------------------ */
  /*  Stato "dati salvati": verifica attributi del carrello               */
  /* ------------------------------------------------------------------ */

  // Cache per evitare fetch ripetuti. Invalidata dopo save/clear.
  let _savedCache = null;

  async function hasSavedBilling() {
    if (_savedCache !== null) return _savedCache;
    try {
      const attrs = await getCartAttributes();
      _savedCache = attrs && attrs['Fattura'] === 'Richiesta' && !!attrs['Tipo soggetto'];
    } catch (e) {
      _savedCache = false;
    }
    return _savedCache;
  }
  function invalidateSavedCache() { _savedCache = null; }

  /* ------------------------------------------------------------------ */
  /*  Gate del bottone checkout                                           */
  /* ------------------------------------------------------------------ */

  const CHECKOUT_MSG_DEFAULT_CART   = 'Compila e salva i dati di fatturazione per procedere al checkout';
  const CHECKOUT_MSG_DEFAULT_DRAWER = 'Compila i dati di fatturazione nel carrello per procedere';
  const CHECKOUT_MSG_INVALID        = 'I dati di fatturazione inseriti non sono validi';
  const CHECKOUT_MSG_UNSAVED        = 'Clicca "Salva dati fatturazione" prima di procedere';

  function setCheckoutState(ctx, { enabled, message }) {
    const btn = document.querySelector(`[data-billing-checkout][data-billing-ctx="${ctx}"]`);
    const errBox = document.querySelector(`[data-billing-checkout-error][data-billing-ctx="${ctx}"]`);
    if (!btn) return;

    btn.disabled = !enabled;
    btn.classList.toggle('is-disabled-billing', !enabled);
    btn.setAttribute('aria-disabled', String(!enabled));

    if (errBox) {
      const txt = errBox.querySelector('[data-billing-checkout-error-text]');
      if (!enabled) {
        if (txt && message) txt.textContent = message;
        errBox.hidden = false;
      } else {
        errBox.hidden = true;
      }
    }
  }

  async function refreshCheckoutGate(ctx) {
    const hasForm = !!document.getElementById(`billing-form--${ctx}`);
    const saved   = await hasSavedBilling();

    // Se il form non esiste in questo contesto (es. drawer senza form):
    // il gate dipende solo dai dati salvati.
    if (!hasForm) {
      setCheckoutState(ctx, {
        enabled: saved,
        message: ctx === 'drawer' ? CHECKOUT_MSG_DEFAULT_DRAWER : CHECKOUT_MSG_DEFAULT_CART,
      });
      return;
    }

    const type = getActiveType(ctx);
    const formValid = isFormValid(ctx, type);

    if (saved) {
      // Se l'utente ha salvato MA poi ha modificato i campi rendendoli
      // invalidi, o ha sporcato il form senza risalvare, riblocchiamo.
      const dirty = isFormDirtyVsSaved(ctx);
      if (dirty && !formValid) {
        setCheckoutState(ctx, { enabled: false, message: CHECKOUT_MSG_INVALID });
        return;
      }
      if (dirty && formValid) {
        setCheckoutState(ctx, { enabled: false, message: CHECKOUT_MSG_UNSAVED });
        return;
      }
      setCheckoutState(ctx, { enabled: true });
      return;
    }

    // Non salvato
    setCheckoutState(ctx, {
      enabled: false,
      message: ctx === 'drawer' ? CHECKOUT_MSG_DEFAULT_DRAWER : CHECKOUT_MSG_DEFAULT_CART,
    });
  }

  function refreshAllCheckoutGates() {
    ['cart', 'drawer'].forEach((ctx) => {
      if (document.querySelector(`[data-billing-checkout][data-billing-ctx="${ctx}"]`)) {
        refreshCheckoutGate(ctx);
      }
    });
  }

  /* Rileva se il form attuale diverge dall'ultimo salvataggio */
  function isFormDirtyVsSaved(ctx) {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (!saved) return true;
    try {
      const { type: savedType, fields: savedFields } = JSON.parse(saved);
      const currentType = getActiveType(ctx);
      if (currentType !== savedType) return true;
      const currentFields = collectFields(ctx, currentType);
      const keys = new Set([...Object.keys(savedFields || {}), ...Object.keys(currentFields)]);
      for (const k of keys) {
        if ((savedFields[k] || '') !== (currentFields[k] || '')) return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Sync altri contesti                                                 */
  /* ------------------------------------------------------------------ */

  function syncOtherContexts(sourceCtx, type, fields) {
    ['cart', 'drawer'].forEach((ctx) => {
      if (ctx === sourceCtx) return;
      if (!document.getElementById(`billing-form--${ctx}`)) return;
      switchType(ctx, type);
      fillFields(ctx, type, fields);
      showSavedCard(ctx, type, fields);
      const cb = document.querySelector(`.billing-clear-btn[data-ctx="${ctx}"]`);
      if (cb) cb.classList.add('is-visible');
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Init per ogni contesto                                              */
  /* ------------------------------------------------------------------ */

  function initContext(ctx) {
    const root = document.getElementById(`billing-form--${ctx}`);
    if (!root) return;
    // Guard: se il root è già stato inizializzato, non raddoppiamo i listener.
    if (root.dataset.billingInited === '1') return;
    root.dataset.billingInited = '1';

    /* Toggle tipo */
    document.querySelectorAll(`.billing-type-btn[data-ctx="${ctx}"]`).forEach((btn) => {
      btn.addEventListener('click', () => {
        switchType(ctx, btn.dataset.type);
        refreshCheckoutGate(ctx);
      });
    });

    /* Validazione blur + refresh gate */
    document.querySelectorAll(`[data-billing-ctx="${ctx}"]`).forEach((input) => {
      input.addEventListener('blur', () => {
        const key = input.dataset.billingKey;
        if (!key || !input.value.trim()) {
          refreshCheckoutGate(ctx);
          return;
        }
        let valid = true;
        if (key === 'Codice Fiscale') valid = validateCF(input.value);
        if (key === 'Partita IVA')    valid = validatePIVA(input.value);
        if (key === 'PEC')            valid = validateEmail(input.value);
        setInputState(input, valid ? 'valid' : 'invalid');
        refreshCheckoutGate(ctx);
      });
      // refresh "morbido" anche durante la digitazione
      input.addEventListener('input', () => refreshCheckoutGate(ctx));
    });

    /* Save */
    const saveBtn = document.querySelector(`.billing-save-btn[data-ctx="${ctx}"]`);
    if (saveBtn) {
      saveBtn.addEventListener('click', async () => {
        const type = getActiveType(ctx);
        if (!validateAll(ctx, type)) return;

        const fields = collectFields(ctx, type);
        const span = saveBtn.querySelector('span');
        saveBtn.disabled = true;
        if (span) span.textContent = 'Salvataggio…';

        try {
          await updateCartAttributes(type, fields);
          invalidateSavedCache();
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ type, fields }));
          showSavedCard(ctx, type, fields);
          const clearBtn = document.querySelector(`.billing-clear-btn[data-ctx="${ctx}"]`);
          if (clearBtn) clearBtn.classList.add('is-visible');
          syncOtherContexts(ctx, type, fields);
          refreshAllCheckoutGates();
        } catch (e) {
          console.error('[billing-form] Errore salvataggio attributi:', e);
          alert('Errore nel salvataggio. Riprova.');
        } finally {
          saveBtn.disabled = false;
          if (span) span.textContent = 'Salva dati fatturazione';
        }
      });
    }

    /* Clear */
    const clearBtn = document.querySelector(`.billing-clear-btn[data-ctx="${ctx}"]`);
    if (clearBtn) {
      clearBtn.addEventListener('click', async () => {
        await clearCartAttributes();
        invalidateSavedCache();
        sessionStorage.removeItem(STORAGE_KEY);
        ['cart', 'drawer'].forEach((c) => {
          if (!document.getElementById(`billing-form--${c}`)) return;
          switchType(c, 'privato');
          document.querySelectorAll(`[data-billing-ctx="${c}"]`).forEach((input) => {
            input.value = '';
            setInputState(input, '');
          });
          hideSavedCard(c);
          const cb = document.querySelector(`.billing-clear-btn[data-ctx="${c}"]`);
          if (cb) cb.classList.remove('is-visible');
        });
        refreshAllCheckoutGates();
      });
    }

    /* Restore da sessionStorage */
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { type, fields } = JSON.parse(saved);
        switchType(ctx, type);
        fillFields(ctx, type, fields);
        showSavedCard(ctx, type, fields);
        if (clearBtn) clearBtn.classList.add('is-visible');
      } catch (e) {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Gate: intercetta click sul bottone checkout disabilitato            */
  /*  (safeguard: oltre all'attributo disabled)                          */
  /*                                                                      */
  /*  Uso delegazione globale perché il drawer e il carrello vengono     */
  /*  re-renderizzati (innerHTML) dopo ogni update: gli handler diretti  */
  /*  sui bottoni andrebbero persi.                                       */
  /* ------------------------------------------------------------------ */

  let _globalGateBound = false;

  function initGlobalGate() {
    if (_globalGateBound) return;
    _globalGateBound = true;

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-billing-checkout]');
      if (!btn) return;
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        e.stopImmediatePropagation();
        const ctx = btn.dataset.billingCtx || 'cart';
        const form = document.getElementById(`billing-form--${ctx}`);
        if (form && typeof form.scrollIntoView === 'function') {
          const details = document.getElementById(`billing-details--${ctx}`);
          if (details && !details.open) details.open = true;
          form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (ctx === 'drawer') {
          // Nel drawer non c'è il form: suggerisci di aprire il carrello.
          const cartLink = document.querySelector('.cart-drawer-buttons a[href*="/cart"]');
          if (cartLink) cartLink.focus();
        }
        return false;
      }
    }, true); // capture: intercetta prima dei submit listener nativi
  }

  /* ------------------------------------------------------------------ */
  /*  Bootstrap                                                           */
  /* ------------------------------------------------------------------ */

  function init() {
    initGlobalGate();
    initContext('cart');
    initContext('drawer');
    refreshAllCheckoutGates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('cart:open', () => {
    initContext('drawer');
    refreshAllCheckoutGates();
  });

  // Dopo ogni update del carrello (quantità, rimozione item, ecc.) il tema
  // rigenera l'HTML: invalidiamo la cache e riverifichiamo lo stato.
  document.addEventListener('cart:updated', () => {
    invalidateSavedCache();
    refreshAllCheckoutGates();
  });
  document.addEventListener('cart:refresh', () => {
    invalidateSavedCache();
    // Dopo il re-render il DOM cambia: re-init contesti
    setTimeout(() => {
      initContext('cart');
      initContext('drawer');
      refreshAllCheckoutGates();
    }, 50);
  });

  // MutationObserver come rete di sicurezza: ogni volta che un bottone
  // data-billing-checkout appare nel DOM (es. dopo re-render drawer),
  // riapplichiamo lo stato corrente.
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-billing-checkout]')) {
          refreshAllCheckoutGates();
          return;
        }
        if (node.querySelector && node.querySelector('[data-billing-checkout]')) {
          refreshAllCheckoutGates();
          return;
        }
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

})();